import React, { useState, useEffect } from 'react';
import logoMain from './assets/main-nb.svg';
import faviconCompact from './assets/favicon-compact.svg';
import './index.css';

const App: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [logoLoaded, setLogoLoaded] = useState(false);
  const [isFirstVisit, setIsFirstVisit] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  useEffect(() => {
    const hasVisited = localStorage.getItem('sthira-visited');
    if (hasVisited) {
      setIsFirstVisit(false);
      setIsLoading(false);
    } else {
      localStorage.setItem('sthira-visited', 'true');
      const timer = setTimeout(() => {
        setIsLoading(false);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleLogoLoad = () => {
    setLogoLoaded(true);
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);
    
    const contactData = {
      name: formData.get('name') as string,
      email: formData.get('email') as string,
      message: formData.get('message') as string
    };

    // Basic validation
    if (!contactData.name || !contactData.email || !contactData.message) {
      setSubmitMessage('Please fill in all required fields.');
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

      const response = await fetch(`${API_BASE_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(contactData),
      });

      const result = await response.json();

      if (result.success) {
        setSubmitMessage('Thank you for your message! We will get back to you soon.');
        form.reset();
      } else {
        setSubmitMessage(result.error || 'Sorry, there was an error sending your message. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      setSubmitMessage('Sorry, there was an error sending your message. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Remove snap scroll for main content */}
      <main className={`transition-all duration-1000 ${isLoading && isFirstVisit ? 'opacity-0' : 'opacity-100'} overflow-y-auto h-screen`}>
      {/* Loading Screen - Only on first visit */}
      {isLoading && isFirstVisit && (
        <div className="fixed inset-0 bg-white z-50 flex items-center justify-center">
          <div className="loading-spinner w-12 h-12 border-4 border-gray-200 border-t-[#555440] rounded-full"></div>
        </div>
      )}

      {/* Sticky Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/90 backdrop-blur-md z-40 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <button 
              className="flex items-center cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#555440] focus:ring-offset-2 rounded-md" 
              onClick={() => scrollToSection('home')}
            >
              <img src={faviconCompact} alt="Sthira Logo" className="h-20 w-20 object-contain" />
              <span className="-ml-5 text-xl font-bold text-gray-800">sthira</span>
            </button>

            {/* Desktop Navigation Menu */}
            <div className="hidden md:flex space-x-8 items-center">
              <button
                onClick={() => scrollToSection('home')}
                className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-[#555440] transition-colors focus:outline-none focus:ring-2 focus:ring-[#555440] focus:ring-offset-2 rounded-md"
              >
                Home
              </button>
              <button
                onClick={() => scrollToSection('what-we-offer')}
                className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-[#555440] transition-colors focus:outline-none focus:ring-2 focus:ring-[#555440] focus:ring-offset-2 rounded-md"
              >
                What we offer
              </button>
              <button
                onClick={() => scrollToSection('who-we-are')}
                className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-[#555440] transition-colors focus:outline-none focus:ring-2 focus:ring-[#555440] focus:ring-offset-2 rounded-md"
              >
                Our story
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-[#555440] transition-colors focus:outline-none focus:ring-2 focus:ring-[#555440] focus:ring-offset-2 rounded-md"
              >
                Contact
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 rounded-md text-gray-700 hover:text-[#555440] hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[#555440] focus:ring-offset-2"
              aria-expanded={isMobileMenuOpen}
              aria-label="Toggle navigation menu"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 shadow-lg">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
              <button
                onClick={() => {
                  scrollToSection('home');
                  setIsMobileMenuOpen(false);
                }}
                className="block w-full text-left px-3 py-2 text-base font-medium text-gray-700 hover:text-[#555440] hover:bg-gray-50 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-[#555440] focus:ring-offset-2"
              >
                Home
              </button>
              <button
                onClick={() => {
                  scrollToSection('what-we-offer');
                  setIsMobileMenuOpen(false);
                }}
                className="block w-full text-left px-3 py-2 text-base font-medium text-gray-700 hover:text-[#555440] hover:bg-gray-50 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-[#555440] focus:ring-offset-2"
              >
                What we offer
              </button>
              <button
                onClick={() => {
                  scrollToSection('who-we-are');
                  setIsMobileMenuOpen(false);
                }}
                className="block w-full text-left px-3 py-2 text-base font-medium text-gray-700 hover:text-[#555440] hover:bg-gray-50 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-[#555440] focus:ring-offset-2"
              >
                Our story
              </button>
              <button
                onClick={() => {
                  scrollToSection('contact');
                  setIsMobileMenuOpen(false);
                }}
                className="block w-full text-left px-3 py-2 text-base font-medium text-gray-700 hover:text-[#555440] hover:bg-gray-50 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-[#555440] focus:ring-offset-2"
              >
                Contact
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Main Content */}
      {/* Each section snaps to start */}
        
        {/* Home Section */}
        <section id="home" className="min-h-screen pt-16 bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              {/* Logo Section */}
              <div className="mb-8">
                <img
                  src={logoMain}
                  alt="Sthira Logo"
                  className={`mx-auto w-64 h-64 md:w-80 md:h-80 object-contain transition-all duration-2000 ${
                    logoLoaded || !isFirstVisit ? 'animate-scale-in animate-float' : 'opacity-0 scale-75'
                  }`}
                  onLoad={handleLogoLoad}
                />
              </div>

              {/* Main Content */}
              <div className={`transition-all duration-1500 delay-500 ${
                logoLoaded || !isFirstVisit ? 'animate-fade-in-up' : 'opacity-0 translate-y-8'
              }`}>
                <h1 className="text-4xl md:text-6xl font-bold text-gray-800 mb-6">
                  <span className="text-[#555440]">Rooted in Purpose.</span><br />
                  <span className="text-gray-700">Powered by Technology.</span>
                </h1>
                
                <p className="text-lg md:text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
                  A balance between human intention, systemic clarity, and technological leverage. 
                  We believe technology should feel as natural as pen on paper.
                </p>

                {/* Teaser Cards */}
                <div className="grid md:grid-cols-3 gap-6 mb-12 max-w-4xl mx-auto">
                  <div className="bg-white/70 backdrop-blur-sm p-6 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                    <h3 className="text-xl font-semibold text-gray-800 mb-3">Digital Enablement</h3>
                    <p className="text-gray-600">Unlock your business potential with thoughtful technology solutions</p>
                  </div>
                  <div className="bg-white/70 backdrop-blur-sm p-6 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                    <h3 className="text-xl font-semibold text-gray-800 mb-3">Cloud-Native Solutions</h3>
                    <p className="text-gray-600">Modern, scalable architectures that grow with your business</p>
                  </div>
                  <div className="bg-white/70 backdrop-blur-sm p-6 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                    <h3 className="text-xl font-semibold text-gray-800 mb-3">Systemic Thinking</h3>
                    <p className="text-gray-600">Simplicity through understanding complexity</p>
                  </div>
                </div>

                {/* Call to Action */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                  <button
                    onClick={() => scrollToSection('what-we-offer')}
                    className="bg-[#555440] hover:bg-[#806660] text-white font-semibold py-3 px-8 rounded-lg transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
                  >
                    Discover Our Services
                  </button>
                  <button
                    onClick={() => scrollToSection('contact')}
                    className="border-2 border-[#555440] text-[#555440] hover:bg-[#555440] hover:text-white font-semibold py-3 px-8 rounded-lg transform hover:scale-105 transition-all duration-300"
                  >
                    Get In Touch
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Background Animation */}
          <div className="fixed inset-0 -z-10 overflow-hidden">
            <div className="absolute -top-40 -right-40 w-80 h-80 bg-gray-200 rounded-full opacity-20 animate-pulse-slow"></div>
            <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gray-300 rounded-full opacity-20 animate-pulse-slow" style={{animationDelay: '1s'}}></div>
          </div>
        </section>

        {/* What We Offer Section */}
        <section id="what-we-offer" className="min-h-screen pt-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-5xl font-bold text-gray-800 mb-6">
                What We <span className="text-[#555440]">Offer</span>
              </h2>
              <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
                At Sthira, we don’t just deliver technology—we engineer momentum.<br />
                Our offerings are designed to meet businesses where they are, and help take them where they want to be. Whether it's navigating complex system integrations, empowering SMEs with scalable tech, or guiding enterprise-wide transformations, we bring clarity, agility, and craftsmanship to the table.<br />
                Each solution is backed by strategic insight and a commitment to purposeful innovation—ensuring that your technology doesn’t just function, but flourishes.
              </p>
            </div>

            {/* Mobile-first animated cards with micro-interactions */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Technology Consulting */}
              <div className="group bg-gray-50 hover:bg-orange-50 p-6 rounded-xl shadow-lg transition-all duration-300 transform hover:scale-105 hover:shadow-xl cursor-pointer animate-fade-in-card">
                <div className="text-4xl mb-4 transition-transform duration-300 group-hover:scale-125" role="img" aria-label="infinity">♾️</div>
                <h3 className="text-lg font-bold text-gray-800 mb-2">Technology Consulting</h3>
                <p className="text-sm text-[#555440] font-semibold mb-2">Strategic alignment for purposeful growth</p>
                <p className="text-gray-600 text-sm">We help you bridge the gap between business goals and technological execution. Our consulting spans system audits, architecture reviews, and roadmap planning—ensuring that every tech decision delivers measurable value and long-term resilience.</p>
              </div>

              {/* Digital Transformation */}
              <div className="group bg-gray-50 hover:bg-orange-50 p-6 rounded-xl shadow-lg transition-all duration-300 transform hover:scale-105 hover:shadow-xl cursor-pointer animate-fade-in-card">
                <div className="text-4xl mb-4 transition-transform duration-300 group-hover:scale-125" role="img" aria-label="spiral">🌀</div>
                <h3 className="text-lg font-bold text-gray-800 mb-2">Digital Transformation</h3>
                <p className="text-sm text-[#555440] font-semibold mb-2">Modernize processes, mindsets, and platforms</p>
                <p className="text-gray-600 text-sm">Transformation is more than just tools—it's a shift in thinking. We guide you through digitizing workflows, redesigning customer experiences, and cultivating internal cultures that embrace agility and innovation. Your business evolves, and so should your tech.</p>
              </div>

              {/* SME Enablement */}
              <div className="group bg-gray-50 hover:bg-orange-50 p-6 rounded-xl shadow-lg transition-all duration-300 transform hover:scale-105 hover:shadow-xl cursor-pointer animate-fade-in-card">
                <div className="text-4xl mb-4 transition-transform duration-300 group-hover:scale-125" role="img" aria-label="growth">🌱</div>
                <h3 className="text-lg font-bold text-gray-800 mb-2">SME Enablement</h3>
                <p className="text-sm text-[#555440] font-semibold mb-2">Smart, scalable solutions for growing enterprises</p>
                <p className="text-gray-600 text-sm">For small and medium-sized businesses, we design IT setups that are practical, cost-effective, and future-ready. From foundational infrastructure to operational tools, we help SMEs lay down a digital core that can flex with ambition and growth.</p>
              </div>

              {/* Cloud & Integration */}
              <div className="group bg-gray-50 hover:bg-orange-50 p-6 rounded-xl shadow-lg transition-all duration-300 transform hover:scale-105 hover:shadow-xl cursor-pointer animate-fade-in-card">
                <div className="text-4xl mb-4 transition-transform duration-300 group-hover:scale-125">
                  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M30 28H12C8.686 28 6 25.314 6 22C6 18.686 8.686 16 12 16C12.34 16 12.677 16.027 13.01 16.08C13.82 12.721 16.86 10 20.5 10C24.642 10 28 13.358 28 17.5C28 18.021 27.953 18.533 27.86 19.03C29.77 19.36 31.25 20.98 31.25 22.92C31.25 25.02 29.52 26.75 27.42 26.75H30C31.657 26.75 33 25.407 33 23.75C33 22.093 31.657 20.75 30 20.75H29.5C29.224 20.75 29 20.526 29 20.25C29 19.974 29.224 19.75 29.5 19.75H30C32.485 19.75 34.5 21.765 34.5 24.25C34.5 26.735 32.485 28.75 30 28.75H30Z" fill="#555440"/>
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-gray-800 mb-2">Cloud & Integration</h3>
                <p className="text-sm text-[#555440] font-semibold mb-2">Seamless systems that talk to each other—beautifully</p>
                <p className="text-gray-600 text-sm">We specialize in API design, data migration, and cloud-native architectures that scale. Whether you're integrating legacy systems, embracing hybrid models, or building modular ecosystems, we make complexity feel effortless.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Who We Are Section */}
        <section id="who-we-are" className="min-h-screen pt-16 bg-gradient-to-br from-gray-50 to-orange-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-5xl font-bold text-gray-800 mb-6">
                About <span className="text-[#555440]">Sthira</span>
              </h2>
            </div>
            <div className="max-w-4xl mx-auto">
              <div className="mb-12">
                <p className="text-lg md:text-xl text-gray-700 mb-6">
                  At Sthira, we believe technology should feel as intuitive and grounding as pen on paper — simple, elegant, and rooted in real human experience.
                </p>
                <p className="text-lg text-gray-600 mb-6">
                  We're a tight-knit collective of technologists and designers united by a shared philosophy: <span className="font-semibold text-[#555440]">Rooted in Purpose. Powered by Technology.</span> That means we begin every engagement with deep intentionality — understanding not just what needs to be built, but why. With that clarity, we channel our technical expertise to craft thoughtful, resilient systems that empower our clients and grow with them.
                </p>
              </div>
              <div className="mb-8">
                <h3 className="text-xl font-bold text-gray-800 mb-4">Our capabilities span:</h3>
                <ul className="list-disc list-inside text-gray-700 space-y-2">
                  <li>Automated Underwriting Systems powered by adaptable AI</li>
                  <li>Payment Gateway Solutions & Integrations</li>
                  <li>Enterprise Consulting with precision and scalability</li>
                  <li>Digital Transformation Initiatives across industries</li>
                  <li>ERP Implementations that streamline operations</li>
                  <li>eCommerce Solutions built for flexibility and growth</li>
                  <li>System Integrations including B2B and B2B2C architectures</li>
                </ul>
              </div>
              <div className="mb-8">
                <h3 className="text-xl font-bold text-gray-800 mb-4">We deliver:</h3>
                <ul className="list-disc list-inside text-gray-700 space-y-2">
                  <li>Tailored, end-to-end solutions designed to meet unique business needs</li>
                  <li>Adaptable software products ready to deploy and scale across both local and global markets</li>
                </ul>
              </div>
              <div className="mt-8 text-lg text-gray-700">
                <span role="img" aria-label="lightbulb">💡</span> More than just consultants, we become collaborators. We embed ourselves in our clients' journeys — from the first spark of an idea to the last line of code, and beyond. Our work is measured not just in deliverables, but in the long-term success stories we help write.
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="min-h-screen pt-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-5xl font-bold text-gray-800 mb-6">
                Let's <span className="text-[#555440]">Connect</span>
              </h2>
              <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
                Let's build something thoughtful, together.
              </p>
            </div>

            <div className="max-w-2xl mx-auto">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#555440] focus:border-transparent transition-all duration-300"
                    placeholder="Your name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#555440] focus:border-transparent transition-all duration-300"
                    placeholder="your.email@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={6}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#555440] focus:border-transparent transition-all duration-300 resize-none"
                    placeholder="Tell us about your project or how we can help..."
                  ></textarea>
                </div>

                <div className="text-center">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`font-semibold py-3 px-8 rounded-lg transform transition-all duration-300 shadow-lg hover:shadow-xl ${
                      isSubmitting 
                        ? 'bg-gray-400 cursor-not-allowed' 
                        : 'bg-[#555440] hover:bg-[#806660] hover:scale-105 text-white'
                    }`}
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </button>
                </div>

                {/* Status message */}
                {submitMessage && (
                  <div className={`text-center p-4 rounded-lg ${
                    submitMessage.includes('Thank you') 
                      ? 'bg-green-50 text-green-700 border border-green-200' 
                      : 'bg-red-50 text-red-700 border border-red-200'
                  }`}>
                    {submitMessage}
                  </div>
                )}
              </form>

              <div className="mt-12 text-center">
                <p className="text-gray-600 mb-4">Or connect with us on:</p>
                <div className="flex justify-center space-x-6">
                  <button className="text-[#555440] hover:text-[#806660] transition-colors transform hover:scale-110" aria-label="LinkedIn">
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                  </button>
                  <button className="text-[#555440] hover:text-[#806660] transition-colors transform hover:scale-110" aria-label="GitHub">
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default App;